/**
 * Embeddings (SIL-10670 companion pipeline) — the DeepInfraMapper's progress
 * and spend.
 *
 * The mapper vectorises video and channel documents (Qwen/Qwen3-Embedding-4B,
 * via DeepInfra) for semantic search. This tab answers three questions an
 * operator actually asks: how much of the inventory is embedded, what is it
 * costing, and is the cache doing its job — the cache is the whole reason the
 * cost stays this small.
 *
 * ONE ENDPOINT, ONE QUERY. Unlike ES activity, `/metrics/embeddings` has no
 * `?days=` — it returns coverage, all four cost/throughput windows, the
 * 30-day daily series and every breakdown in a single payload, so
 * `useEmbeddings()` is cached once (`api/queries.ts`) and every tile below
 * reads the same cache entry rather than issuing its own request. That is a
 * server-side batching detail, not a presentation choice: the cost and
 * throughput tiles still show only the ONE window the board's RangeChips
 * currently have selected — the same convention EsActivity, ChannelHeadline
 * and VideoHeadline use for their own range-scoped figures — picking their
 * row out of the four the endpoint already sent rather than issuing a
 * second, disagreeing fetch.
 *
 * DEFENSIVE READS THROUGHOUT. The response is validated against `Embeddings`
 * in `api/schemas.ts` at the fetch boundary, so a genuinely malformed payload
 * never reaches here — it surfaces as the panel's own error state instead.
 * But a backend built in parallel against a written contract is the likelier
 * failure than a malicious one, and a field that is merely late or renamed
 * must not crash the tab it belongs to. So every read below still uses
 * optional chaining and an explicit fallback rather than assuming the shape
 * the schema promises actually arrived.
 */
import { useEmbeddings } from '../api/queries'
import { rangeLabel, useRange } from '../api/range'
import { StackedBar } from '../components/charts'
import { PanelState, Stat, Tile } from '../components/ui'
import { headline, n, pct, usd } from '../components/format'
import type {
  Embeddings as EmbeddingsPayload,
  EmbeddingsBreakdownRow,
  EmbeddingsCoverageService,
  EmbeddingsThroughputWindow,
} from '../api/schemas'

/* ---------------------------------------------------------------------------
 * Coverage — embedded / pending / failed / deferred against total, per
 * document type. This is "how many processed or not", stated the way
 * Coverage.tsx states it for the collector: a percent-complete headline
 * backed by the exact counts it was computed from.
 *
 * THE HEADLINE IS `embedded / total`, NOT `done / total` (SIL-10670
 * coverage-counts-vectors fix). `done` alone undercounts: `channel_
 * classification` carries ~3.29M documents a predecessor GPU pipeline
 * embedded before this pipeline's `embed_state` bookkeeping ever existed --
 * they HAVE a vector and are genuinely finished, but never got the `done`
 * marker written, so reading `done` as "how much is embedded" reported a
 * COMPLETE channel pipeline as 13.1% done with 3,236,399 falsely "pending".
 * `embedded` (`done + pre_existing`, computed server-side --
 * collector/domain/embeddings.py's `EmbeddingCoverageCounts.embedded`) is
 * what "no more work needed" actually means, and both fields fall back
 * (`embedded ?? done`, `pre_existing ?? 0`) so a board reading an OLDER
 * collector that has not deployed this fix still renders -- reproducing
 * exactly the pre-fix arithmetic on that data, never a crash and never an
 * invented number.
 * ------------------------------------------------------------------------- */

const SERVICES: ReadonlyArray<['video' | 'channel', string]> = [
  ['video', 'video documents'],
  ['channel', 'channel documents'],
]

export function EmbeddingsCoverage() {
  const { data, isLoading, error } = useEmbeddings()

  return (
    <>
      {SERVICES.map(([key, label]) => {
        const m: EmbeddingsCoverageService | undefined = data?.coverage?.[key]
        const total = m?.total ?? 0
        // `embedded` falls back to `done + pre_existing` against a payload
        // that omits `embedded` itself but still carries `pre_existing`
        // (server sends a partial addition), and further to bare `done`
        // against an OLDER collector that has neither field yet -- see the
        // module comment above for why that innermost fallback is exactly
        // the pre-fix arithmetic, not a broken one.
        const embedded = m ? (m.embedded ?? (m.done ?? 0) + (m.pre_existing ?? 0)) : 0
        const preExisting = m?.pre_existing ?? 0
        // NEVER a percentage over a zero (or absent, or negative) denominator
        // — that reads as "0% complete", the exact shape of a broken
        // pipeline, when the true state is "nothing in scope yet".
        const donePct = m && total > 0 ? (embedded / total) * 100 : null

        return (
          <Tile key={key} title={`Coverage · ${label}`}
                hint="embedded / pending / failed / deferred">
            <PanelState isLoading={isLoading} error={error}>
              {!m || total <= 0 ? (
                <p className="panel-load">
                  {!m ? 'no coverage reported' : `no ${label} in scope yet`}
                </p>
              ) : (
                <>
                  <div className="cov-head">
                    <div className={`cov-pct tnum mono ${
                      (donePct ?? 0) >= 95 ? 'ok' : 'warn'}`}>
                      {pct(donePct)}
                    </div>
                    <div className="cov-pct-l">embedded<br />of total</div>
                  </div>
                  <StackedBar
                    label={`${label}: ${pct(donePct)} embedded`}
                    segments={[
                      { name: 'embedded', value: embedded, state: 'ok' },
                      { name: 'pending', value: m.pending ?? 0, state: 'flow' },
                      { name: 'deferred', value: m.deferred ?? 0, state: 'warn' },
                      { name: 'failed', value: m.failed ?? 0, state: 'crit' },
                    ]}
                  />
                  <table className="t" style={{ marginTop: 14 }}>
                    <tbody>
                      <tr>
                        <td>total</td>
                        <td className="n">{n(total)}</td>
                        <td className="n q">100%</td>
                      </tr>
                      <tr>
                        <td>embedded</td>
                        <td className="n" style={{ color: 'var(--ok)' }}>{n(embedded)}</td>
                        <td className="n q">{pct(donePct)}</td>
                      </tr>
                      {/* Honest, not hidden: most of a channel's coverage came
                          from the earlier GPU run, not this pipeline, and an
                          operator may care which. Shown as two sub-rows of
                          `embedded` (they sum to it) only when the backend
                          reports the split AND there is something to show --
                          an older collector, or a document type with no
                          legacy population (video), renders neither row
                          rather than a pair of zeros nobody asked about. */}
                      {m.pre_existing != null && preExisting > 0 ? (
                        <tr className="q">
                          <td>· pre-existing (legacy GPU run)</td>
                          <td className="n">{n(preExisting)}</td>
                          <td className="n q">
                            {pct(total > 0 ? (preExisting / total) * 100 : null)}
                          </td>
                        </tr>
                      ) : null}
                      {m.pre_existing != null && preExisting > 0 ? (
                        <tr className="q">
                          <td>· embedded by this pipeline</td>
                          <td className="n">{n(m.done ?? 0)}</td>
                          <td className="n q">
                            {pct(total > 0 ? ((m.done ?? 0) / total) * 100 : null)}
                          </td>
                        </tr>
                      ) : null}
                      <tr>
                        <td>pending</td>
                        <td className="n">{n(m.pending ?? null)}</td>
                        <td className="n q">
                          {pct(total > 0 && m.pending != null ? (m.pending / total) * 100 : null)}
                        </td>
                      </tr>
                      <tr>
                        <td>deferred</td>
                        <td className="n" style={{ color: 'var(--warning)' }}>{n(m.deferred ?? null)}</td>
                        <td className="n q">
                          {pct(total > 0 && m.deferred != null ? (m.deferred / total) * 100 : null)}
                        </td>
                      </tr>
                      <tr>
                        <td>failed</td>
                        <td className="n" style={{ color: (m.failed ?? 0) > 0 ? 'var(--crisis)' : undefined }}>
                          {n(m.failed ?? null)}
                        </td>
                        <td className="n q">
                          {pct(total > 0 && m.failed != null ? (m.failed / total) * 100 : null)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  {(m.failed ?? 0) > 0 ? (
                    <p className="panel-err" style={{ marginTop: 10 }}>
                      {n(m.failed ?? null)} {label} failed embedding — check the mapper's error log.
                    </p>
                  ) : null}
                </>
              )}
            </PanelState>
          </Tile>
        )
      })}
    </>
  )
}

/* ---------------------------------------------------------------------------
 * Dead-letter — documents PAID FOR (DeepInfra billed the embedding call) and
 * then PERMANENTLY LOST: mirrors-embedding retries an Elasticsearch write up
 * to 3 times, and once every retry has failed the document is marked
 * embed_state="failed" AND recorded in `embedding_dead_letter` so the pending
 * query never re-picks it. That combination — billed AND unrecoverable — is
 * a different and more serious fact than EmbeddingsCoverage's `failed`
 * column (which is still being retried and may yet succeed), which is why it
 * gets its own tile with its own severity language rather than folding into
 * that one.
 *
 * ZERO READS AS CALM, NON-ZERO READS AS LOUD — the same ok/crit state
 * vocabulary Stat already carries elsewhere on this tab (EmbeddingsCoverage's
 * `donePct`/`failed`, Vidiq.tsx's staleness Stat) rather than a new visual
 * language. Split per spec (video vs channel) as well as totalled: which
 * pipeline is losing paid-for work is exactly the fact an aggregate-only
 * number would hide.
 *
 * OPTIONAL ON THE WIRE, same reason `throughput` is below: this field was
 * added to the contract after the tab first shipped, so a collector that has
 * not deployed it yet is an ordinary day-one state, not a broken payload.
 * ------------------------------------------------------------------------- */

export function EmbeddingsDeadLetter() {
  const { data, isLoading, error } = useEmbeddings()
  const dl = data?.dead_letter
  const total = dl?.total ?? 0
  const healthy = !dl || total <= 0

  return (
    <Tile
      title="Dead-lettered"
      hint="paid for, permanently lost — exhausted 3 write retries"
    >
      <PanelState isLoading={isLoading} error={error}>
        {!data ? null : !dl ? (
          <p className="panel-load">not reported by this backend yet</p>
        ) : (
          <>
            <div className="stats-row">
              {/* `headline()` for consistency with every other stat cell. A
                  dead-letter count should never reach seven digits, but if it
                  ever did, that is precisely the moment the number must stay
                  readable rather than ellipsising into something smaller. */}
              <Stat label="video documents" value={headline(dl.video)}
                    state={dl.video > 0 ? 'crit' : 'ok'} />
              <Stat label="channel documents" value={headline(dl.channel)}
                    state={dl.channel > 0 ? 'crit' : 'ok'} />
              <Stat label="total" value={headline(dl.total)}
                    state={healthy ? 'ok' : 'crit'} />
            </div>
            {healthy ? (
              <p className="note">
                none — every embedding attempt has either succeeded or is still being retried.
              </p>
            ) : (
              <p className="panel-err" style={{ marginTop: 10 }}>
                {n(dl.total)} document{dl.total === 1 ? '' : 's'} dead-lettered —
                paid for and permanently lost, check the mapper's error log.
              </p>
            )}
          </>
        )}
      </PanelState>
    </Tile>
  )
}

/* ---------------------------------------------------------------------------
 * The four windows the board's own RangeChips offer, and the server's
 * cost/throughput payloads are keyed on -- as a literal union rather than a
 * bare `string` so a lookup by the globally-selected `days` narrows safely
 * instead of widening to `any`.
 *
 * SHARED BY COST AND THROUGHPUT, because both read the SAME selection the
 * SAME way: reuse the day the board's own RangeChips already selected to
 * pick ONE row out of the four the endpoint serves -- never add a second
 * range control that could disagree with the first, and never show all four
 * at once (a table of every window here previously read as "throughput
 * collapsing over time" when nothing of the sort was happening -- the four
 * numbers were the same numerator over four different-sized denominators).
 * ------------------------------------------------------------------------- */
const BAND_KEYS = ['1', '2', '7', '30'] as const
type Band = (typeof BAND_KEYS)[number]
function isBand(s: string): s is Band {
  return (BAND_KEYS as readonly string[]).includes(s)
}

/* ---------------------------------------------------------------------------
 * Cost — ONE window: whichever the board's RangeChips currently have
 * selected. The endpoint still serves all four bands in a single payload (see
 * the schema comment on `Embeddings.cost`), but that is a server-side
 * batching detail, not a reason to show four rows -- every other range-scoped
 * figure on this board (EsActivity's Stat row, ChannelHeadline/VideoHeadline)
 * presents the selected window's own numbers, not an enumeration of every
 * window the server happens to have computed.
 * ------------------------------------------------------------------------- */

export function EmbeddingsCost() {
  const { data, isLoading, error } = useEmbeddings()
  const { days } = useRange()
  const selected = String(days)
  const c = data && isBand(selected) ? data.cost?.[selected] : undefined
  // NEVER a percentage over a zero (or absent, or negative) denominator —
  // the same rule EmbeddingsCoverage's donePct follows.
  const hitRate = c && c.texts > 0 ? (c.cache_hits / c.texts) * 100 : null

  return (
    <Tile
      title="Embedding cost"
      hint={`${rangeLabel(days)} window`
        + (data?.model && data?.price_per_million_tokens_usd != null
          ? ` · ${data.model} · ${usd(data.price_per_million_tokens_usd)}/1M tokens`
          : '')}
    >
      <PanelState isLoading={isLoading} error={error}>
        {!data ? null : !c ? (
          <p className="panel-load">not reported for the {rangeLabel(days)} window</p>
        ) : (
          <>
            <div className="stats-row">
              {/* `headline()`, not `n()` — token counts here run to eight and
                  nine digits (36,791,2.. observed live) and a full-precision
                  figure overflows its neighbour, then ellipsises to
                  "36,791,2…", which reads as a far SMALLER number than it is.
                  Same reason every other stat cell on the board uses it.
                  `Stat` keeps the exact value in its `title`, so precision is
                  deferred to a hover rather than lost. */}
              <Stat label="spend" value={usd(c.usd)} />
              <Stat label="tokens" value={headline(c.tokens)} />
              <Stat label="requests" value={headline(c.requests)} />
              <Stat label="cache hit" value={pct(hitRate)} />
            </div>
            {/* Cache effectiveness, stated in prose too — it is the mapper's
                whole economic justification (a text already embedded costs
                nothing to re-serve), so it does not live only inside a Stat
                an operator might skim past. */}
            <p className="note">
              {hitRate === null
                ? `No texts embedded in the ${rangeLabel(days)} window — cache effectiveness is not yet measurable.`
                : `${pct(hitRate)} of texts over ${rangeLabel(days)} were served from cache `
                  + `(${n(c.cache_hits)} of ${n(c.texts)}) — that is what keeps repeat cost near zero.`}
            </p>
          </>
        )}
      </PanelState>
    </Tile>
  )
}

/* ---------------------------------------------------------------------------
 * Throughput — the piece of "how many processed or not, country/category/
 * language wise" this tab was missing: how fast. TWO DIFFERENT MEASUREMENTS
 * share this card now, and the card must never let a viewer confuse them:
 *
 *   - texts/s and tokens/s are AVERAGES OVER THE WINDOW. `throughput` is
 *     derived server-side from the exact same embedding_usage rows `cost`
 *     sums, and that table is aggregated per DAY — there is no per-second
 *     signal in it. The server names every field `avg_*` for that reason: a
 *     mapper that embeds in short bursts and sits idle the rest of the window
 *     reports a MUCH lower number here than its true rate while running —
 *     easily two orders of magnitude, not a rounding difference.
 *
 *   - docs/s HEADLINES THE COLLECTOR'S OWN LIVE RATE when the payload
 *     carries one (`live`, SIL-10670 live-throughput headline): two samples
 *     of the completed-documents counters, measured server-side over a
 *     trailing ~minute span — the same two-samples-of-a-monotonic-counter
 *     reading Throughput.tsx presents for the collector tab, computed where
 *     the counters live. It supersedes this card's previous client-side
 *     between-two-manual-refreshes approximation (useLiveDocRate, removed
 *     with it), which could only answer after a second press of Refresh.
 *     When live is measured, the window-average docs/s demotes to the Stat's
 *     `sub` slot ("7d avg …"), still tracking the range chips.
 *
 * NULL IS NOT ZERO, restated here because this is now the third place on the
 * board the rule carries (Queues.rate_per_s, Throughput.per_s): live
 * docs_per_s === null means the collector cannot measure right now — the
 * window average stays primary and the sub reads "live —" with a hover saying
 * the measurement is warming up. 0.0 means measured-and-genuinely-idle and
 * headlines as "0.0000/s", never a dash and never a bare `0` (the stray-0
 * footgun this tile has been explicitly audited for; see DocsStat below).
 *
 * OPTIONAL ON THE WIRE, all of it. `throughput` was added to the contract
 * after this tab first shipped; `live` was added after `throughput`. A
 * collector that has deployed neither still renders every other tile — and
 * one without `live` renders exactly the pre-live presentation (window
 * averages primary, no live affordance at all — a permanent "live —" against
 * a backend that will never send one would be a promise, not a fact).
 * ------------------------------------------------------------------------- */

/** No rate is ever computed from a zero (or missing, or non-finite)
 *  denominator — a dash, matching the rule the rest of this panel follows
 *  for percentages (EmbeddingsCoverage, the cache-hit rate above). A REAL
 *  zero rate (nothing embedded in that window) is a different, equally true
 *  answer and renders as `0.0000/s`, the same distinction EmbeddingsCost
 *  draws for a genuinely zero spend versus an unmeasurable one.
 *
 *  PRECISION WIDENS BELOW 1/s, the same reason format.ts's `usd()` widens
 *  below $1: this tab's real `avg_*_per_s` figures are typically a few
 *  thousandths (a few hundred texts spread over an 86,400 s day), and a bare
 *  two-decimal format prints "0.00/s" for nearly everything this tile
 *  actually reports -- indistinguishable from a genuine zero, which is
 *  precisely the confusion this whole feature exists to prevent. */
function rate(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—'
  return `${(Math.abs(v) < 1 ? v.toFixed(4) : v.toFixed(2))}/s`
}

/** The hover for a `live —`: the server IS reporting live rates, it just
 *  cannot state this one yet. Not Throughput.tsx's "press Refresh again to
 *  measure a rate" — pressing Refresh here is not the sampling action any
 *  more (the collector samples on its own clock), so borrowing that wording
 *  would instruct an action that does not cause the measurement. */
const LIVE_WARMING_HINT =
  'the collector cannot measure a live rate yet — measurement is warming up'

/** The one docs/s Stat, in whichever of its three honest states the payload
 *  supports — live and average sharing the SAME card (the `sub` slot,
 *  Vidiq.tsx's existing idiom for a secondary related figure) rather than a
 *  second row a viewer could mistake for another window:
 *
 *  1. `live === undefined` — no live source at all (an older collector, or a
 *     partial/hostile `live` object missing this subject): exactly the
 *     pre-live rendering, window average primary, NO live affordance — a
 *     permanent dash against a backend that will never fill it would be a
 *     promise dressed as a measurement.
 *  2. `live === null` (or a non-finite/negative impossibility — mirroring
 *     the collector's own backwards-counter guard, a rate that cannot be
 *     true renders as unmeasured rather than as nonsense): window average
 *     stays primary, sub reads "live —" with the warming-up hover.
 *  3. a real number, 0.0 INCLUDED: the live figure takes the primary slot,
 *     the label says so ("live docs/s"), and the window average demotes to
 *     the sub ("7d avg …"), still driven by the selected range chip.
 *
 *  NEVER a bare truthiness check on the rate. `{v && …}` would print a raw,
 *  unformatted "0" for a genuinely idle mapper (0.0 is a real, measured
 *  answer, not an absent one) — the exact rendering bug this tile has been
 *  audited against. Every branch above is an explicit `=== undefined` /
 *  `=== null` test, and a genuine zero renders through `rate()` as
 *  "0.0000/s". */
function DocsStat({ service, avg, live, avgLabel }: {
  /** row prefix ('video' / 'channel'); undefined for the combined headline */
  service?: string | undefined
  avg: number | null | undefined
  live: number | null | undefined
  /** the selected range chip's own label ('7d') — names the demoted average */
  avgLabel: string
}) {
  const base = service === undefined ? 'docs/s' : `${service} docs/s`
  if (live === undefined) return <Stat label={base} value={rate(avg)} />
  if (live === null || !Number.isFinite(live) || live < 0) {
    return (
      <Stat label={base} value={rate(avg)}
            sub={<>{'live '}<span className="q" title={LIVE_WARMING_HINT}>—</span></>} />
    )
  }
  return (
    <Stat label={service === undefined ? 'live docs/s' : `${service} live docs/s`}
          value={rate(live)}
          sub={`${avgLabel} avg ${rate(avg)}`} />
  )
}

/* ---------------------------------------------------------------------------
 * The four windows section above was ONE window here too, same reasoning as
 * EmbeddingsCost -- and it matters MORE here than there. Four rows of this
 * figure previously read as throughput COLLAPSING over time (21.92/s down to
 * 0.73/s), when it was really one numerator divided by four different-sized
 * denominators (window_days × 86,400s). A single figure removes the false
 * trend outright; the average-not-live framing below is what stops that one
 * remaining figure from being misread as the mapper's live or peak rate
 * instead.
 *
 * `by_service` (SIL-10670 follow-on) answers the actual ask this card was
 * missing: "add both channel and video doc throughput". Reusing the SAME
 * Stat/stats-row presentation as the combined row above, not a table --
 * see the module comment on BAND_KEYS for why a table of this exact figure
 * is the shape that misled before.
 * ------------------------------------------------------------------------- */
const THROUGHPUT_SERVICES: ReadonlyArray<['video' | 'channel', string]> = [
  ['video', 'video'],
  ['channel', 'channel'],
]

export function EmbeddingsThroughput() {
  const { data, isLoading, error, refetch, isFetching } = useEmbeddings()
  const { days } = useRange()
  const selected = String(days)
  const perDoc = data?.texts_per_document
  const t: EmbeddingsThroughputWindow | undefined =
    data?.throughput && isBand(selected) ? data.throughput[selected] : undefined
  const byService = t?.by_service
  const live = data?.live

  /* Defensive reads, per the module docstring: the schema promises the three
     subjects arrive together once `live` exists, but a payload built in
     parallel that ships a partial object must degrade per-figure (to the
     pre-live rendering) rather than crash the tile. `undefined` = no live
     source; `null` = the server says "unmeasurable right now"; a number
     (0.0 included) = the genuine rate. */
  const liveDocs = (svc: 'video' | 'channel' | 'combined'): number | null | undefined =>
    live?.[svc]?.docs_per_s

  const liveCombined = liveDocs('combined')
  const liveHeadline = liveCombined !== undefined && liveCombined !== null
    && Number.isFinite(liveCombined) && liveCombined >= 0
  /* Rounded for the caption ("trailing 62s window"), and only claimed when
     the server actually reported the span -- a live figure with an absent
     span still headlines, it just does not name a window it cannot state. */
  const spanS = live?.span_s
  const span = typeof spanS === 'number' && Number.isFinite(spanS) && spanS > 0
    ? Math.round(spanS) : null

  return (
    <Tile
      title="Throughput"
      /* The caption carries the split truthfully in BOTH states: with a live
         figure headlining, "everything here is a window average" would be a
         lie, so the caption names which figure is live (and over what span)
         and which remain averages. Without one, the original wording stands
         unchanged -- on an older collector this card IS all window averages,
         and must keep saying so. */
      hint={liveHeadline
        ? `docs/s: live${span !== null ? ` · trailing ${span}s window` : ''}`
          + ` · texts/s & tokens/s: ${rangeLabel(days)} window average`
        : `${rangeLabel(days)} window · average over the window, including idle time — not a live rate`}
      /* Every tile on this tab shares ONE `useEmbeddings()` query, so this
         refetches the whole tab, not just this card -- the same thing the
         other panels' refresh buttons do with their own shared queries.
         Matches Vidiq.tsx / Throughput.tsx exactly (`btn`, disabled while
         fetching, lowercase label) rather than introducing a second look.
         No longer the live-rate sampling action (the collector samples on
         its own clock now); a press simply fetches the newest reading. */
      action={
        <button className="btn" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? 'reading…' : 'refresh'}
        </button>
      }
    >
      <PanelState isLoading={isLoading} error={error}>
        {!data ? null : !data.throughput ? (
          <p className="panel-load">not reported by this backend yet</p>
        ) : !t ? (
          <p className="panel-load">not reported for the {rangeLabel(days)} window</p>
        ) : (
          <>
            <div className="stats-row">
              <Stat label="texts/s" value={rate(t.avg_texts_per_s)} />
              <DocsStat avg={t.avg_docs_per_s} live={liveDocs('combined')}
                        avgLabel={rangeLabel(days)} />
              <Stat label="tokens/s" value={rate(t.avg_tokens_per_s)} />
            </div>
            {/* Stated in prose too, the same reason EmbeddingsCost repeats its
                cache-hit story below its Stats: which figure is live and which
                is an average is the one fact on this tile that must not be
                skimmed past. The texts-per-document divisor is pinned to the
                AVERAGE figure explicitly once a live rate headlines -- the
                live rate counts completed documents directly and assumes
                nothing. */}
            <p className="note">
              {liveHeadline
                ? `docs/s is the collector's own live measurement`
                  + `${span !== null ? ` over the trailing ${span}s` : ''} — a genuine `
                  + `right-now rate. texts/s and tokens/s are still averages across the `
                  + `${rangeLabel(days)} window (${n(days)} × 86,400s), including any idle time.`
                  + (perDoc != null
                    ? ` The ${rangeLabel(days)} avg docs/s assumes ${n(perDoc)} texts embedded per document.`
                    : '')
                : `Average across the ${rangeLabel(days)} window (${n(days)} × 86,400s), including any `
                  + `idle time — not the mapper's peak or instantaneous rate.`
                  + (perDoc != null ? ` docs/s assumes ${n(perDoc)} texts embedded per document.` : '')}
            </p>

            <div className="sec">
              <h2>by service</h2>
              <span className="hint">{live !== undefined
                ? 'docs/s: live where measured · texts & tokens: avg over the window'
                : 'avg over the window'}</span>
              <span className="rule" />
            </div>
            {!byService ? (
              <p className="panel-load">not reported by this backend yet</p>
            ) : (
              THROUGHPUT_SERVICES.map(([key, label]) => {
                const svc = byService[key]
                return (
                  <div className="stats-row" key={key}>
                    <Stat label={`${label} texts/s`} value={rate(svc.avg_texts_per_s)} />
                    <DocsStat service={label} avg={svc.avg_docs_per_s}
                              live={liveDocs(key)} avgLabel={rangeLabel(days)} />
                    <Stat label={`${label} tokens/s`} value={rate(svc.avg_tokens_per_s)} />
                  </div>
                )
              })
            )}
            {/* Only against a backend that reports live rates at all -- an
                older collector's card is honestly all-averages and needs no
                live story to explain. */}
            {live !== undefined ? (
              <p className="note">
                {'"live" is measured server-side by the collector'
                  + (span !== null ? ` over the trailing ${span}s` : '')
                  + ' — documents actually completed just now, not a window average.'
                  + ' "live —" means the measurement is still warming up.'}
              </p>
            ) : null}
          </>
        )}
      </PanelState>
    </Tile>
  )
}

/* ---------------------------------------------------------------------------
 * Breakdowns — country / category / language, done vs total per bucket, so
 * partial coverage is visible rather than only volume. An EMPTY array is the
 * backend's way of saying a field is not aggregatable yet (see the schema
 * comment); rendering that as an empty table would claim zero traffic for a
 * dimension that was simply never asked, which is a different and worse
 * claim than "we don't have this yet".
 * ------------------------------------------------------------------------- */

function BreakdownTable({ title, rows }: { title: string; rows: readonly EmbeddingsBreakdownRow[] | undefined }) {
  const list = rows ?? []
  if (list.length === 0) {
    return (
      <div>
        <div className="sec"><h2>{title}</h2><span className="rule" /></div>
        <p className="note">
          not available — the backend does not aggregate embeddings by {title} yet
        </p>
      </div>
    )
  }
  const sorted = [...list].sort((a, b) => (b?.total ?? 0) - (a?.total ?? 0))
  return (
    <div>
      <div className="sec"><h2>{title}</h2><span className="rule" /></div>
      <table className="t">
        <thead>
          <tr><th>{title}</th><th className="n">total</th><th className="n">done</th><th className="n">%</th></tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => {
            const total = r?.total ?? 0
            const done = r?.done ?? null
            const donePct = total > 0 && done !== null ? (done / total) * 100 : null
            return (
              <tr key={r?.key ?? i}>
                <td className="q">{r?.key ?? '—'}</td>
                <td className="n">{n(r?.total ?? null)}</td>
                <td className="n">{n(done)}</td>
                <td className="n">{pct(donePct)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export function EmbeddingsBreakdowns() {
  const { data, isLoading, error } = useEmbeddings()
  const b: EmbeddingsPayload['breakdown'] | undefined = data?.breakdown

  return (
    <Tile title="Breakdowns" hint="country · category · language — done vs total">
      <PanelState isLoading={isLoading} error={error}>
        {!data ? null : (
          <>
            <BreakdownTable title="country" rows={b?.country} />
            <BreakdownTable title="category" rows={b?.category} />
            <BreakdownTable title="language" rows={b?.language} />
          </>
        )}
      </PanelState>
    </Tile>
  )
}
